import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import api from "@/lib/api";
import { CalendarDays, Plus, Check, X, Clock, Users, BarChart2,
  ChevronLeft, ChevronRight, Loader2, Umbrella, AlertTriangle, Trash2, Star
} from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { EmployeeSelector } from "@/components/EmployeeSelector";
import { getCCQInfo, getJoursFeriesForMonth, getCongesCCQForMonth } from "@/lib/ccq-holidays";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPE_LABELS = {
  vacances: { label: "Vacances", color: "bg-blue-100 text-blue-700", emoji: "🏖️" },
  maladie: { label: "Maladie", color: "bg-red-100 text-red-700", emoji: "🏥" },
  personnel: { label: "Personnel", color: "bg-purple-100 text-purple-700", emoji: "👤" },
  sans_solde: { label: "Sans solde", color: "bg-slate-100 text-slate-700", emoji: "📋" },
};

const STATUT_LABELS = {
  en_attente: { label: "En attente", color: "bg-yellow-100 text-yellow-700" },
  approuve: { label: "Approuvé", color: "bg-green-100 text-green-700" },
  refuse: { label: "Refusé", color: "bg-red-100 text-red-700" },
};

const MONTHS_FR = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const DAYS_FR = ["D","L","M","M","J","V","S"];

// ---- CalendrierConges Component ----
const CalendrierConges = ({ conges, visibleFilter }) => {
  const today = new Date();
  const [current, setCurrent] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selectedDay, setSelectedDay] = useState(null); // Pour afficher le dialogue
  const [showDayDialog, setShowDayDialog] = useState(false);

  const daysInMonth = new Date(current.year, current.month + 1, 0).getDate();
  const firstDayOfWeek = new Date(current.year, current.month, 1).getDay(); // 0=Dimanche

  // Récupérer les jours fériés et congés CCQ pour ce mois
  const joursFeries = getJoursFeriesForMonth(current.year, current.month);
  const congesCCQ = getCongesCCQForMonth(current.year, current.month);

  const getCongesForDay = (day) => {
    const dayStr = `${current.year}-${String(current.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return conges.filter(c => c.date_debut <= dayStr && dayStr <= c.date_fin);
  };

  const getCCQForDay = (day) => {
    const dayStr = `${current.year}-${String(current.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return getCCQInfo(dayStr);
  };

  const handleDayClick = (day) => {
    const dayConges = getCongesForDay(day);
    if (dayConges.length > 0) {
      setSelectedDay(day);
      setShowDayDialog(true);
    }
  };

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setCurrent(p => {
          const d = new Date(p.year, p.month - 1); return { year: d.getFullYear(), month: d.getMonth() };
        })}><ChevronLeft className="w-4 h-4" /></Button>
        <h3 className="font-semibold text-lg">{MONTHS_FR[current.month]} {current.year}</h3>
        <Button variant="outline" size="sm" onClick={() => setCurrent(p => {
          const d = new Date(p.year, p.month + 1); return { year: d.getFullYear(), month: d.getMonth() };
        })}><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {/* Congés obligatoires CCQ du mois */}
      {congesCCQ.length > 0 && (
        <div className="space-y-1.5">
          {congesCCQ.map((c, i) => (
            <div key={c.id || c._id || i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200" data-testid={`ccq-conge-banner-${i}`}>
              <Star className="w-4 h-4 text-orange-600 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-orange-800">{c.nom}</p>
                <p className="text-[10px] text-orange-600">{c.debut} au {c.fin}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="w-full">
        <div className="grid grid-cols-7 gap-1 w-full">
        {DAYS_FR.map((d, i) => (
          <div key={`${d}-${i}`} className="text-center text-xs font-medium text-slate-500 py-1">{d}</div>
        ))}
        {cells.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const dayConges = getCongesForDay(day);
          const ccq = getCCQForDay(day);
          const isToday = day === today.getDate() && current.month === today.getMonth() && current.year === today.getFullYear();
          const isFerie = !!ccq.jourFerie;
          const isCongeCCQDay = !!ccq.conge;
          const isWeekend = new Date(current.year, current.month, day).getDay() === 0 || new Date(current.year, current.month, day).getDay() === 6;

          // Styling: priorité fériés > congés CCQ > today > normal
          let cellClass = 'border-slate-100 bg-white hover:bg-slate-50';
          if (isFerie) cellClass = 'border-red-300 bg-red-50';
          else if (isCongeCCQDay) cellClass = 'border-orange-300 bg-orange-50';
          else if (isToday) cellClass = 'border-blue-500 bg-blue-50';
          else if (isWeekend) cellClass = 'border-slate-100 bg-slate-50/50';

          return (
            <div key={day} 
              className={`min-h-[56px] p-0.5 border rounded text-xs ${cellClass} ${dayConges.length > 0 ? 'cursor-pointer hover:ring-2 hover:ring-blue-400' : ''}`}
              title={isFerie ? ccq.jourFerie.nom : isCongeCCQDay ? ccq.conge.nom : ''}
              onClick={() => handleDayClick(day)}>
              <div className="flex items-center justify-between">
                <span className={`font-medium text-[11px] ${
                  isFerie ? 'text-red-700' : isCongeCCQDay ? 'text-orange-700' : isToday ? 'text-blue-600' : 'text-slate-700'
                }`}>{day}</span>
                {isFerie && <Star className="w-2.5 h-2.5 text-red-500 fill-red-500" />}
              </div>
              <div className="space-y-0.5 mt-0.5">
                {isFerie && (
                  <div className="text-[8px] px-0.5 rounded truncate leading-tight bg-red-100 text-red-700 font-medium">
                    {ccq.jourFerie.nom.length > 12 ? ccq.jourFerie.nom.substring(0, 12) + '...' : ccq.jourFerie.nom}
                  </div>
                )}
                {!isFerie && isCongeCCQDay && dayConges.length === 0 && (
                  <div className="text-[8px] px-0.5 rounded truncate leading-tight bg-orange-100 text-orange-700 font-medium">
                    CCQ
                  </div>
                )}
                {dayConges.slice(0, isFerie ? 1 : 2).map((c, i) => {
                  const type = TYPE_LABELS[c.type_conge] || TYPE_LABELS.personnel;
                  return (
                    <div key={i} title={`${c.employee_nom} - ${type.label}`}
                      className={`text-[9px] px-0.5 rounded truncate leading-tight ${type.color}`}>
                      {c.employee_nom?.split(' ')[0]}
                    </div>
                  );
                })}
                {dayConges.length > (isFerie ? 1 : 2) && <div className="text-slate-400 text-[9px]">+{dayConges.length - (isFerie ? 1 : 2)}</div>}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Légende — filtrée par types visibles + CCQ */}
      <div className="flex flex-wrap gap-3 pt-2 border-t">
        <div className="flex items-center gap-1">
          <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium flex items-center gap-1">
            <Star className="w-3 h-3 fill-red-500 text-red-500" /> Jour férié CCQ
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">Congé CCQ</span>
        </div>
        {Object.entries(TYPE_LABELS).filter(([k]) => !visibleFilter || visibleFilter.includes(k)).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1">
            <span className={`text-xs px-2 py-0.5 rounded ${v.color}`}>{v.emoji} {v.label}</span>
          </div>
        ))}
      </div>

      {/* Liste des jours fériés CCQ du mois */}
      {joursFeries.length > 0 && (
        <div className="pt-2 border-t">
          <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
            <Star className="w-3.5 h-3.5 text-red-500 fill-red-500" /> Jours fériés CCQ ce mois
          </p>
          <div className="flex flex-wrap gap-2">
            {joursFeries.map((jf, i) => (
              <div key={jf.id || jf._id || i} className="text-xs px-2 py-1 rounded bg-red-50 border border-red-200 text-red-700" data-testid={`ccq-ferie-${i}`}>
                <span className="font-semibold">{parseInt(jf.date.split('-')[2])}</span> — {jf.nom}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Dialogue pour afficher tous les employés en congé ce jour */}
      <Dialog open={showDayDialog} onOpenChange={setShowDayDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedDay && `Employés en congé le ${selectedDay} ${MONTHS_FR[current.month]} ${current.year}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {selectedDay && getCongesForDay(selectedDay).map((c, idx) => {
              const type = TYPE_LABELS[c.type_conge] || TYPE_LABELS.personnel;
              return (
                <div key={c.id || idx} className="p-3 border border-slate-200 rounded-lg bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{c.employee_nom}</p>
                      <div className="mt-1 space-y-1">
                        <p className="text-sm text-slate-600">
                          📅 {c.date_debut} → {c.date_fin}
                        </p>
                        <p className="text-sm text-slate-600">
                          🕒 {c.nombre_jours} jour{c.nombre_jours > 1 ? 's' : ''}
                        </p>
                        <Badge className={`text-xs mt-1 ${type.color}`}>
                          {type.emoji} {type.label}
                        </Badge>
                      </div>
                    </div>
                    <Badge className={STATUT_LABELS[c.statut]?.color || 'bg-slate-100 text-slate-700'}>
                      {STATUT_LABELS[c.statut]?.label || c.statut}
                    </Badge>
                  </div>
                  {c.notes_employe && (
                    <p className="text-sm text-slate-600 mt-2 pl-2 border-l-2 border-slate-300">
                      💬 {c.notes_employe}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowDayDialog(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---- Main Component ----
export const GestionConges = () => {
  const { user } = useAuth();
  const { canDo } = usePermissions() || {};
  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via matrice.
  //   - `conges_admin_voir_tous` : filtrer congés (tous vs siens), afficher boutons
  //     approuver/refuser, sélecteur employé dans modal, filtre calendrier.
  //   - `conges_gerer_soldes` : afficher l'onglet "Soldes" + bouton "Modifier solde".
  //   Le backend (Vague 2b) fait déjà l'ownership scoping — fail-secure ici.
  const canVoirTousConges = !!(canDo && canDo('conges_admin_voir_tous'));
  const canGererSoldes    = !!(canDo && canDo('conges_gerer_soldes'));

  const [conges, setConges] = useState([]);
  const [calendrierConges, setCalendrierConges] = useState([]);
  const [soldes, setSoldes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showRefusModal, setShowRefusModal] = useState(false);
  const [selectedConge, setSelectedConge] = useState(null);
  const [motifRefus, setMotifRefus] = useState("");
  const [filterStatut, setFilterStatut] = useState("tous");
  const [editSolde, setEditSolde] = useState(null);
  const [savingSolde, setSavingSolde] = useState(false);
  const [employeeCongesConfig, setEmployeeCongesConfig] = useState({});

  // Types de congés visibles pour cet employé
  const visibleTypes = canVoirTousConges ? Object.keys(TYPE_LABELS) : Object.keys(TYPE_LABELS).filter(key => {
    const cfg = employeeCongesConfig?.[key];
    return cfg?.visible !== false;
  });

  const [form, setForm] = useState({
    employee_id: user?.employee_id || "",
    type_conge: "",
    date_debut: "",
    date_fin: "",
    notes: ""
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Charger chaque endpoint indépendamment pour ne pas bloquer la page si un seul échoue
      const [congesRes, calRes, soldesRes, empRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/conges`).then(r => r.data),
        axios.get(`${API_URL}/api/conges/calendrier`).then(r => r.data),
        axios.get(`${API_URL}/api/conges/soldes-tous`).then(r => r.data),
        // 🆕 [Phase 31 #1] include_inactive=true pour permettre à l'admin RH
        // de voir l'historique de congés des employés devenus inactifs.
        axios.get(`${API_URL}/api/employees?include_inactive=true`).then(r => r.data),
      ]);
      
      const cData = congesRes.status === 'fulfilled' ? congesRes.value : {};
      const calData = calRes.status === 'fulfilled' ? calRes.value : {};
      const sData = soldesRes.status === 'fulfilled' ? soldesRes.value : {};
      const empData = empRes.status === 'fulfilled' ? empRes.value : {};

      const allConges = cData.conges || [];
      const filtered = canVoirTousConges ? allConges : allConges.filter(c => c.employee_id === user?.employee_id);
      setConges(filtered);
      
      const allCal = calData.conges || [];
      const filteredCal = canVoirTousConges ? allCal : allCal.filter(c => c.employee_id === user?.employee_id);
      setCalendrierConges(filteredCal);

      // Fusionner les congés rétroactifs (du calendrier) dans la liste des demandes
      const retroItems = filteredCal
        .filter(c => c.retroactif === true)
        .map(r => ({
          ...r,
          id: `retro-${r.employee_id}-${r.date_debut}`,
          nb_jours: parseFloat(r.nombre_jours || r.nb_jours || 1),
          retroactif: true,
          statut: "approuve"
        }));
      setConges([...filtered, ...retroItems]);

      setSoldes(sData.soldes || []);
      setEmployees(empData.employees || []);
      
      // Log les erreurs silencieusement
      if (soldesRes.status === 'rejected') console.warn("Erreur soldes:", soldesRes.reason);
      if (calRes.status === 'rejected') console.warn("Erreur calendrier:", calRes.reason);
      
      if (user?.employee_id) {
        try {
          const empRes2 = await axios.get(`${API_URL}/api/employees/${user.employee_id}`);
          const empDataSingle = empRes2.data;
          setEmployeeCongesConfig(empDataSingle?.conges_config || {});
        } catch (e) {
          console.error('Erreur notification SMS congé:', e);
        }
      }
    } catch (e) { toast.error("Erreur chargement"); console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.employee_id || !form.date_debut || !form.date_fin) {
      toast.error("Veuillez remplir tous les champs obligatoires"); return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/conges`, form);
      const data = res.data;
      if (res.status !== 200) throw new Error(data.detail || "Erreur");
      toast.success("✅ Demande soumise avec succès !");
      setShowNewModal(false);
      setForm({ employee_id: user?.employee_id || "", type_conge: "vacances", date_debut: "", date_fin: "", notes: "" });
      loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const handleApprouver = async (conge) => {
    try {
      const res = await axios.put(`${API_URL}/api/conges/${conge.id}/approuver`, { approuve_par: `${user?.prenom} ${user?.nom}` });
      if (res.status === 200) { toast.success("✅ Congé approuvé"); loadAll(); }
      else { const e = res.data; toast.error(e.detail); }
    } catch (e) { toast.error("Erreur"); }
  };

  const handleRefuser = async () => {
    if (!selectedConge) return;
    try {
      const res = await axios.put(`${API_URL}/api/conges/${selectedConge.id}/refuser`, { motif_refus: motifRefus, approuve_par: `${user?.prenom} ${user?.nom}` });
      if (res.status === 200) { toast.success("Congé refusé"); setShowRefusModal(false); setMotifRefus(""); loadAll(); }
      else { const e = res.data; toast.error(e.detail); }
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDelete = async (conge) => {
    if (!window.confirm("Annuler cette demande de congé ?")) return;
    try {
      const res = await axios.delete(`${API_URL}/api/conges/${conge.id}`);
      if (res.status === 200) { toast.success("Demande annulée"); loadAll(); }
    } catch (e) { toast.error("Erreur"); }
  };

  const saveSolde = async (solde) => {
    setSavingSolde(true);
    try {
      const res = await axios.put(`${API_URL}/api/conges/solde/${solde.employee_id}`, {
        vacances_total: parseFloat(solde.vacances_total)
      });
      if (res.status === 200) { toast.success("✅ Solde sauvegardé"); setEditSolde(null); loadAll(); }
      else { toast.error("Erreur sauvegarde"); }
    } catch (e) { toast.error("Erreur"); }
    finally { setSavingSolde(false); }
  };

  const filteredConges = filterStatut === "tous" ? conges : conges.filter(c => c.statut === filterStatut);
  const pending = conges.filter(c => c.statut === "en_attente");

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays className="w-7 h-7 text-blue-600" />
            Gestion des Congés
          </h1>
          <p className="text-slate-500 text-sm">Demandes de vacances, maladies et absences</p>
        </div>
        <Button onClick={() => setShowNewModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-conge-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle demande
        </Button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-xs text-yellow-600 font-medium">En attente</p>
                <p className="text-2xl font-bold text-yellow-700">{pending.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-green-600 font-medium">Approuvées</p>
                <p className="text-2xl font-bold text-green-700">{conges.filter(c=>c.statut==='approuve').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-blue-600 font-medium">Total demandes</p>
                <p className="text-2xl font-bold text-blue-700">{conges.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-slate-600" />
              <div>
                <p className="text-xs text-slate-600 font-medium">Jours pris (approu.)</p>
                <p className="text-2xl font-bold text-slate-700">
                  {conges.filter(c=>c.statut==='approuve').reduce((s,c)=>s+(c.nb_jours||0),0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="calendrier">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="calendrier" data-testid="tab-calendrier"><CalendarDays className="w-4 h-4 mr-2" />Calendrier</TabsTrigger>
          <TabsTrigger value="demandes" data-testid="tab-demandes">
            <Clock className="w-4 h-4 mr-2" />Demandes
            {pending.length > 0 && <Badge variant="destructive" className="ml-2 text-xs">{pending.length}</Badge>}
          </TabsTrigger>
          {canGererSoldes && <TabsTrigger value="soldes"><BarChart2 className="w-4 h-4 mr-2" />Soldes</TabsTrigger>}
        </TabsList>

        {/* CALENDRIER */}
        <TabsContent value="calendrier" className="mt-4">
          <Card><CardContent className="pt-6">
            <CalendrierConges conges={calendrierConges} visibleFilter={canVoirTousConges ? null : visibleTypes} />
          </CardContent></Card>
        </TabsContent>

        {/* DEMANDES */}
        <TabsContent value="demandes" className="mt-4 space-y-3">
          <div className="flex gap-2">
            {["tous", "en_attente", "approuve", "refuse"].map(s => (
              <Button key={s} size="sm" variant={filterStatut === s ? "default" : "outline"}
                onClick={() => setFilterStatut(s)}>
                {s === "tous" ? "Tous" : STATUT_LABELS[s]?.label}
                <Badge variant="outline" className="ml-2 text-xs">
                  {s === "tous" ? conges.length : conges.filter(c => c.statut === s).length}
                </Badge>
              </Button>
            ))}
          </div>

          {filteredConges.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-500">
              <Umbrella className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>Aucune demande de congé</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {filteredConges.map(conge => {
                const type = TYPE_LABELS[conge.type_conge] || TYPE_LABELS.personnel;
                const statut = STATUT_LABELS[conge.statut] || {};
                return (
                  <Card key={conge.id} className={conge.statut === 'en_attente' ? 'border-yellow-200' : ''}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <EmployeeAvatar photoUrl={conge.employee_photo} prenom={(conge.employee_nom || '?').split(' ')[0]} nom={(conge.employee_nom || '').split(' ').slice(1).join(' ')} size="sm" />
                          <div>
                            <p className="font-medium text-sm">{conge.employee_nom}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`text-xs px-2 py-0.5 rounded ${type.color}`}>{type.emoji} {type.label}</span>
                              <span className="text-xs text-slate-500">
                                {conge.date_debut} → {conge.date_fin} <strong>({conge.nb_jours} j.)</strong>
                              </span>
                            </div>
                            {conge.notes && <p className="text-xs text-slate-400 mt-0.5">{conge.notes}</p>}
                            {conge.motif_refus && <p className="text-xs text-red-500 mt-0.5">Motif refus: {conge.motif_refus}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {conge.retroactif ? (
                            <Badge className="bg-slate-100 text-slate-600 text-xs">Rétroactif</Badge>
                          ) : (
                            <>
                              <Badge className={`${statut.color} text-xs`}>{statut.label}</Badge>
                              {canVoirTousConges && conge.statut === 'en_attente' && (
                                <>
                                  <Button size="sm" variant="ghost" className="text-green-600 hover:bg-green-50 h-8 w-8 p-0"
                                    onClick={() => handleApprouver(conge)}><Check className="w-4 h-4" /></Button>
                                  <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                                    onClick={() => { setSelectedConge(conge); setShowRefusModal(true); }}>
                                    <X className="w-4 h-4" /></Button>
                                </>
                              )}
                              {conge.statut === 'en_attente' && (
                                <Button size="sm" variant="ghost" className="text-slate-400 hover:bg-slate-50 h-8 w-8 p-0"
                                  onClick={() => handleDelete(conge)}><Trash2 className="w-4 h-4" /></Button>
                              )}
                              {canVoirTousConges && conge.statut !== 'en_attente' && (
                                <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-50 h-8 w-8 p-0"
                                  onClick={() => handleDelete(conge)} title="Supprimer ce congé"><Trash2 className="w-4 h-4" /></Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* SOLDES */}
        {canGererSoldes && (
          <TabsContent value="soldes" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-blue-600" />
                  Soldes de congés {new Date().getFullYear()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {soldes.map(s => (
                    <div key={s.employee_id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <EmployeeAvatar photoUrl={s.employee_photo} prenom={(s.employee_nom || '?').split(' ')[0]} nom={(s.employee_nom || '').split(' ').slice(1).join(' ')} size="sm" />
                          <div>
                            <p className="font-medium text-sm">{s.employee_nom}</p>
                            <Badge variant={s.permanence === 'saisonnier' ? 'outline' : 'default'} className="text-xs">
                              {s.permanence === 'saisonnier' ? 'Saisonnier' : 'Permanent'}
                            </Badge>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => setEditSolde({ ...s })}>
                          Modifier solde
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        {[
                          { key: "vacances", label: "Vacances", color: "text-blue-600", emoji: "🏖️" },
                          { key: "maladie", label: "Maladie", color: "text-red-600", emoji: "🏥" },
                          { key: "personnel", label: "Personnel", color: "text-purple-600", emoji: "👤" },
                        ].filter(({ key }) => {
                          const cfg = s.conges_config || {};
                          const catCfg = cfg[key];
                          if (!catCfg || typeof catCfg !== 'object') return true;
                          return catCfg.visible !== false;
                        }).map(({ key, label, color, emoji }) => {
                          const cfg = s.conges_config || {};
                          const catCfg = (typeof cfg[key] === 'object') ? cfg[key] : {};
                          const isNoLimit = catCfg.aucune_limite === true;
                          const total = s[`${key}_total`] || 0;
                          const utilise = s[`${key}_utilise`] || 0;
                          const restant = isNoLimit ? null : (total - utilise);
                          return (
                          <div key={key} className="text-center bg-slate-50 rounded p-2">
                            <p className="text-xs text-slate-500">{emoji} {label}</p>
                            {isNoLimit ? (
                              <p className={`text-lg font-bold ${color}`}>Illimité</p>
                            ) : (
                              <p className={`text-lg font-bold ${color}`}>{restant?.toFixed(0)} <span className="text-xs font-normal text-slate-400">/ {total} j</span></p>
                            )}
                            <p className="text-xs text-slate-400">utilisés: {utilise}</p>
                          </div>
                        );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Modal: Nouvelle demande */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouvelle demande de congé</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {canVoirTousConges && (
              <div>
                <Label>Employé *</Label>
                {/* 🆕 [Phase 31 #1] EmployeeSelector — includeInactive pour permettre
                    à l'admin RH d'inscrire un congé historique pour un employé
                    devenu inactif. */}
                <EmployeeSelector
                  employees={employees}
                  value={form.employee_id}
                  onChange={(id) => setForm(p => ({ ...p, employee_id: id }))}
                  includeInactive={true}
                  placeholder="Sélectionner un employé"
                  data-testid="conges-employee-selector"
                />
              </div>
            )}
            <div>
              <Label>Type de congé *</Label>
              <Select value={form.type_conge} onValueChange={v => setForm(p => ({ ...p, type_conge: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).filter(([k]) => {
                    if (canVoirTousConges) {
                      const emp = employees.find(e => e.id === form.employee_id);
                      const cfg = emp?.conges_config?.[k];
                      return cfg === undefined ? true : cfg.visible !== false;
                    }
                    return visibleTypes.includes(k);
                  }).map(([k, v]) => <SelectItem key={k} value={k}>{v.emoji} {v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date de début *</Label>
                <Input type="date" value={form.date_debut} onChange={e => setForm(p => ({ ...p, date_debut: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label>Date de fin *</Label>
                <Input type="date" value={form.date_fin} onChange={e => setForm(p => ({ ...p, date_fin: e.target.value }))} className="mt-1" />
              </div>
            </div>
            {form.date_debut && form.date_fin && form.date_debut <= form.date_fin && (
              <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                📅 Cette période couvre environ {Math.max(0, Math.floor((new Date(form.date_fin) - new Date(form.date_debut)) / 86400000) + 1)} jours calendrier
              </p>
            )}
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="mt-1" placeholder="Raison ou précisions..." />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowNewModal(false)}>Annuler</Button>
            <Button onClick={handleCreate} className="bg-blue-600">Soumettre la demande</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Motif de refus */}
      <Dialog open={showRefusModal} onOpenChange={setShowRefusModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Refuser la demande</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Refus de la demande de <strong>{selectedConge?.employee_nom}</strong> ({selectedConge?.date_debut} → {selectedConge?.date_fin})
            </p>
            <div>
              <Label>Motif de refus (optionnel)</Label>
              <Textarea value={motifRefus} onChange={e => setMotifRefus(e.target.value)} rows={3} className="mt-1" placeholder="Ex: Période trop chargée, demande trop tardive..." />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowRefusModal(false)}>Annuler</Button>
            <Button onClick={handleRefuser} className="bg-red-600 hover:bg-red-700">Confirmer le refus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Modifier solde */}
      {editSolde && (
        <Dialog open={!!editSolde} onOpenChange={() => setEditSolde(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Modifier le solde — {editSolde.employee_nom}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              {[
                { key: "vacances_total", label: "🏖️ Jours vacances total", color: "text-blue-600" },
                { key: "maladie_total", label: "🏥 Jours maladie total", color: "text-red-600" },
                { key: "personnel_total", label: "👤 Jours personnels total", color: "text-purple-600" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input type="number" step="0.5" min="0" value={editSolde[key]} className="mt-1"
                    onChange={e => setEditSolde(p => ({ ...p, [key]: e.target.value }))} />
                </div>
              ))}
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setEditSolde(null)}>Annuler</Button>
              <Button onClick={() => saveSolde(editSolde)} disabled={savingSolde} className="bg-blue-600">
                {savingSolde ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Sauvegarder
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
